import { useState } from 'react'
import { PersonDto, PersonLiteDto } from '../../api/person/models/PersonDto'
import { HelixCandidateLiteDto } from '../../api/role/models/CandidateDto'
import { formatDuration } from '../../utils/dateUtils'
import {
    formatText,
    getCountryFlag,
    getProfilePicUrl,
    getSortedWorkedAts,
} from '../../utils/personUtils'
import ExpandableText from '../ExpandableText'
import { TransformedResolvedPerson } from './HelixCandidateResultList'
import { MapExtra } from '../../utils/MapExtra'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLinkedin } from '@fortawesome/free-brands-svg-icons'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import { CompanyDto } from '../../api/company/models/CompanyDto'

interface Props {
    candidate: HelixCandidateLiteDto
    person: PersonDto
    transformedResolvedPerson?: TransformedResolvedPerson
    personLiteCache: MapExtra<string, PersonLiteDto>
    companyCache: MapExtra<string, CompanyDto>
    onCandidateSelection?: (candidateId: string) => void
}

export default function HelixCandidateResultItem({
    candidate,
    person,
    transformedResolvedPerson,
    personLiteCache,
    companyCache,
    onCandidateSelection,
}: Props): React.JSX.Element {
    const sortedJobs = getSortedWorkedAts(person?.workedAts ?? [])
    const [showAllJobs, setShowAllJobs] = useState(false)
    const displayedJobs = showAllJobs ? sortedJobs : sortedJobs.slice(0, 4)
    const [expandedCompanyIds, setExpandedCompanyIds] = useState<Set<string>>(
        new Set(),
    )

    function toggleCompany(companyId: string) {
        setExpandedCompanyIds((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(companyId)) {
                newSet.delete(companyId)
            } else {
                newSet.add(companyId)
            }
            return newSet
        })
    }

    return (
        <div className="p-4 border-b">
            <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                {/* Avatar (in left col, top row) */}
                <div className="row-span-1">
                    <img
                        src={getProfilePicUrl(person, true)}
                        className="w-14 h-14 rounded-full object-cover"
                        onError={(
                            e: React.SyntheticEvent<HTMLImageElement>,
                        ) => {
                            e.currentTarget.onerror = null // Prevent infinite loop if fallback image also fails
                            e.currentTarget.src = '/no-profile-pic.png'
                        }}
                    />
                </div>

                {/* Name and title (in right col, top row) */}
                <div className="flex justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <span
                                onClick={() =>
                                    onCandidateSelection?.(
                                        candidate.candidatePersonId,
                                    )
                                }
                                className="text-blue-800 font-semibold text-lg leading-tight hover:underline cursor-pointer"
                                role="button"
                            >
                                {person.firstName} {person.lastName}
                            </span>

                            {person.linkedInUrl && (
                                <div className="text-gray-500 text-xl hover:text-linkedin-blue transition-colors duration-200">
                                    <a
                                        href={person.linkedInUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FontAwesomeIcon icon={faLinkedin} />
                                    </a>
                                </div>
                            )}
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                            <div className="flex mt-[-0.5rem] flex-col">
                                <div>{person?.headline}</div>
                                <div className="text-gray-600 text-sm">
                                    {getCountryFlag(
                                        person?.location
                                            ?.split(',')
                                            .pop()
                                            ?.trim() ?? '',
                                    )}
                                    <span className="ml-2">
                                        {person?.location}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* TODO: 77 - comment out these scores, they are just for debugging */}
                    <div className="text-xs text-gray-500 min-w-[210px]">
                        <div># {candidate.rank}</div>
                        <div>Combined: {candidate.combinedScore}</div>
                        <div>
                            Boolean Search Score:{' '}
                            {candidate.booleanSearchScoreNormalised ?? '-'}
                        </div>
                        <div>
                            Aggregated Relationship Score:{' '}
                            {candidate.aggregatedRelationshipScore ?? '-'}
                        </div>
                    </div>
                </div>

                {/* Fields */}
                <Label>Experience</Label>
                <Content>
                    {displayedJobs.map((workedAt) => {
                        const start = workedAt.startDate
                        const end = workedAt.endDate

                        return (
                            <div key={workedAt.id} className="mb-0.5">
                                <span className="font-bold text-gray-900">
                                    {workedAt.jobTitle}
                                </span>
                                <span className="text-gray-500"> at </span>
                                <span className="text-gray-900">
                                    {workedAt.company?.name}
                                </span>
                                {start && (
                                    <span className="italic text-gray-500">
                                        {` · ${new Date(
                                            start,
                                        ).getFullYear()} - ${
                                            end
                                                ? new Date(end).getFullYear()
                                                : 'Present'
                                        } · ${
                                            formatDuration(start, end).duration
                                        }`}
                                    </span>
                                )}
                            </div>
                        )
                    })}

                    {sortedJobs.length > 4 && (
                        <button
                            className="text-xs text-blue-600 mt-1 hover:underline"
                            onClick={() => setShowAllJobs((prev) => !prev)}
                        >
                            {showAllJobs
                                ? 'Show less'
                                : `Show all (${sortedJobs.length})`}
                        </button>
                    )}
                </Content>

                <Label>Network</Label>
                <Content>
                    {(!transformedResolvedPerson?.companies ||
                        transformedResolvedPerson.companies.length === 0) && (
                        <div className="text-gray-500 text-sm">-</div>
                    )}

                    {transformedResolvedPerson?.companies?.map(
                        (company, index) => {
                            const isExpanded = expandedCompanyIds.has(
                                company.companyId,
                            )
                            const visibleConnections = isExpanded
                                ? company.connections
                                : company.connections.slice(0, 3)

                            const isFirst = index === 0
                            const isLast =
                                index ===
                                transformedResolvedPerson?.companies?.length - 1

                            const className = `${
                                !isLast ? 'border-b' : ''
                            } pb-2 ${!isFirst ? 'pt-2' : ''}`.trim()

                            const companyDto = companyCache.getOrThrow(
                                company.companyId,
                            )

                            return (
                                <div key={index} className={className}>
                                    <div className="flex items-center gap-2 mb-2 ml-1">
                                        <FontAwesomeIcon
                                            icon={faBuilding}
                                            className="text-ctxt-blue"
                                        />
                                        <h3 className="text-base font-semibold">
                                            {companyDto.name}
                                        </h3>
                                    </div>

                                    <div className="text-sm text-gray-800">
                                        {visibleConnections.map(
                                            (connection, idx) => {
                                                const seedId =
                                                    connection.seedPersonId
                                                const seedPerson =
                                                    personLiteCache.getOrThrow(
                                                        seedId,
                                                    )
                                                const seedTitles =
                                                    connection.seedPersonJobTitles.join(
                                                        ' | ',
                                                    )
                                                const duration = formatDuration(
                                                    connection.overallStart,
                                                    connection.overallEnd,
                                                ).duration

                                                return (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <img
                                                            src={getProfilePicUrl(
                                                                seedPerson,
                                                                true,
                                                            )}
                                                            className="rounded-full w-6 h-6 mt-0.5"
                                                            onError={(
                                                                e: React.SyntheticEvent<HTMLImageElement>,
                                                            ) => {
                                                                e.currentTarget.onerror =
                                                                    null
                                                                e.currentTarget.src =
                                                                    '/no-profile-pic.png'
                                                            }}
                                                        />
                                                        <div>
                                                            <span className="font-medium">
                                                                {
                                                                    seedPerson.firstName
                                                                }{' '}
                                                                {
                                                                    seedPerson.lastName
                                                                }
                                                            </span>
                                                            {' | '}
                                                            {seedTitles}
                                                            {connection.overallStart && (
                                                                <span className="text-gray-500 italic">
                                                                    {` · ${new Date(
                                                                        connection.overallStart,
                                                                    ).getFullYear()} - ${
                                                                        connection.overallEnd
                                                                            ? new Date(
                                                                                  connection.overallEnd,
                                                                              ).getFullYear()
                                                                            : 'Present'
                                                                    } · ${duration}`}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            },
                                        )}

                                        {company.connections.length > 3 && (
                                            <button
                                                className="text-xs text-blue-600 mt-1 hover:underline"
                                                onClick={() =>
                                                    toggleCompany(
                                                        company.companyId,
                                                    )
                                                }
                                            >
                                                {isExpanded
                                                    ? 'Show less'
                                                    : `Show all (${company.connections.length})`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        },
                    )}
                </Content>

                <Label>About</Label>
                <Content>
                    <ExpandableText
                        className="text-sm whitespace-pre-line text-gray-600"
                        text={formatText(person.summary ?? '-')}
                        maxLines={4}
                    />
                </Content>
            </div>
        </div>
    )
}

function Label({ children }: { children: React.ReactNode }) {
    return (
        <div className="text-sm font-bold text-gray-600 pt-2 text-right mr-4">
            {children}
        </div>
    )
}

function Content({ children }: { children: React.ReactNode }) {
    return <div className="text-sm text-gray-800 pt-2">{children}</div>
}
